import colors from "tailwindcss/colors";
import {
  EngagementSurveyReportGroupBy,
  EngagementSurveyReportResponderFragment,
  EngagementSurveyReportRowRollupFragment,
  EngagementSurveyRollupType,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import { assertNonNull } from "@helpers/helpers";

import { TeamPickerTeam } from "../team-picker";
import {
  EngagementSurveyFilterMatchType,
  EngagementSurveyReportFilterItem,
  EngagementSurveyReportFilterType,
  EngagementSurveyReportViewOptions,
} from "./types";

export const npsColors: string[] = [
  colors.red[600],
  colors.red[400],
  colors.amber[400],
  colors.emerald[500],
  colors.emerald[700],
];

export const rangeColors: string[] = [
  colors.purple[200],
  colors.purple[300],
  colors.purple[400],
  colors.purple[500],
  colors.purple[700],
];

export const getNpsColorValue = (npsScore: number) => {
  if (npsScore <= 0) {
    return npsColors[0];
  }
  if (npsScore <= 30) {
    return npsColors[1];
  }
  if (npsScore <= 50) {
    return npsColors[2];
  }
  if (npsScore <= 70) {
    return npsColors[3];
  }
  return npsColors[4];
};

export const getRangeColorValue = (normalizedValue: number) => {
  if (normalizedValue <= 20) {
    return rangeColors[0];
  }
  if (normalizedValue <= 40) {
    return rangeColors[1];
  }
  if (normalizedValue <= 60) {
    return rangeColors[2];
  }
  if (normalizedValue <= 80) {
    return rangeColors[3];
  }
  return rangeColors[4];
};

export const isHighContrastBackground = (backgroundColor: string) =>
  [npsColors[2], rangeColors[0], rangeColors[1], "#ffffff"].includes(
    backgroundColor,
  );

export const isFilterEqual = (
  a: EngagementSurveyReportFilterItem,
  b: EngagementSurveyReportFilterItem,
) => {
  return a.type === b.type;
};

export function getFilterByType(
  filters: EngagementSurveyReportFilterItem[],
  type: EngagementSurveyReportFilterType.EMPLOYEE_START_DATE,
): (string | null)[] | undefined;
export function getFilterByType(
  filters: EngagementSurveyReportFilterItem[],
  type: EngagementSurveyReportFilterType.TEAM,
): TeamPickerTeam[] | undefined;
export function getFilterByType(
  filters: EngagementSurveyReportFilterItem[],
  type: EngagementSurveyReportFilterType.REPORTS_TO,
): BasicUser[] | undefined;
export function getFilterByType(
  filters: EngagementSurveyReportFilterItem[],
  type: EngagementSurveyReportFilterType.REPORTS_TO_FULL_TREE,
): BasicUser[] | undefined;
export function getFilterByType(
  filters: EngagementSurveyReportFilterItem[],
  type: EngagementSurveyReportFilterType.LOCATION,
): string[] | undefined;
export function getFilterByType(
  filters: EngagementSurveyReportFilterItem[],
  type: EngagementSurveyReportFilterType,
) {
  const filtersOfType = filters.filter((f) => f.type === type);
  if (filtersOfType.length === 0) {
    return undefined;
  }
  return filtersOfType.reduce(
    (acc, filter) => acc.concat(filter.data),
    [] as unknown[],
  );
}

export const hasFilterType = (
  filters: EngagementSurveyReportFilterItem[],
  type: EngagementSurveyReportFilterType,
) => {
  return filters.some((f) => {
    return isFilterEqual(f, { type, data: [] });
  });
};

export function updateFilters(
  existingFilters: EngagementSurveyReportFilterItem[],
  type: EngagementSurveyReportFilterType.EMPLOYEE_START_DATE,
  data: (string | null)[],
): EngagementSurveyReportFilterItem[];
export function updateFilters(
  existingFilters: EngagementSurveyReportFilterItem[],
  type: EngagementSurveyReportFilterType.TEAM,
  data: TeamPickerTeam[],
): EngagementSurveyReportFilterItem[];
export function updateFilters(
  existingFilters: EngagementSurveyReportFilterItem[],
  type: EngagementSurveyReportFilterType.REPORTS_TO,
  data: BasicUser[],
): EngagementSurveyReportFilterItem[];
export function updateFilters(
  existingFilters: EngagementSurveyReportFilterItem[],
  type: EngagementSurveyReportFilterType.REPORTS_TO_FULL_TREE,
  data: BasicUser[],
): EngagementSurveyReportFilterItem[];
export function updateFilters(
  existingFilters: EngagementSurveyReportFilterItem[],
  type: EngagementSurveyReportFilterType.LOCATION,
  data: string[],
): EngagementSurveyReportFilterItem[];
export function updateFilters(
  existingFilters: EngagementSurveyReportFilterItem[],
  type: EngagementSurveyReportFilterType,
  data: TeamPickerTeam[] | (string | null)[] | BasicUser[] | string[],
): EngagementSurveyReportFilterItem[];
export function updateFilters(
  existingFilters: EngagementSurveyReportFilterItem[],
  type: EngagementSurveyReportFilterType,
  data: TeamPickerTeam[] | (string | null)[] | BasicUser[] | string[],
) {
  if (hasFilterType(existingFilters, type)) {
    return existingFilters.map((filter) => {
      if (
        isFilterEqual(filter, {
          type,
          data,
        } as EngagementSurveyReportFilterItem)
      ) {
        return {
          type,
          data,
        };
      }
      return filter;
    });
  }

  return [...existingFilters, { type, data }];
}

export const getResponderKey = (
  responder:
    | EngagementSurveyReportResponderFragment
    | EngagementSurveyReportRowRollupFragment,
): string => {
  switch (responder.rollupType) {
    case EngagementSurveyRollupType.Team:
      return `${assertNonNull(responder.team?.id)}`;
    case EngagementSurveyRollupType.CareerTrack:
      return `${assertNonNull(responder.careerTrack?.id)}`;
    case EngagementSurveyRollupType.Location:
      return assertNonNull(responder.location);
    case EngagementSurveyRollupType.IsManager:
      return responder.isManager ? "Managers" : "ICs";
    case EngagementSurveyRollupType.Manager:
      return `${assertNonNull(responder.manager?.id)}`;
    case EngagementSurveyRollupType.Organization:
      return "Org";
    case EngagementSurveyRollupType.Collapsed:
      return "Collapsed";
  }
};

export const getResponderLabel = (
  responder: EngagementSurveyReportResponderFragment,
): string => {
  switch (responder.rollupType) {
    case EngagementSurveyRollupType.Team:
      return assertNonNull(responder.team?.title);
    case EngagementSurveyRollupType.CareerTrack:
      return assertNonNull(responder.careerTrack?.title);
    case EngagementSurveyRollupType.Location:
      return assertNonNull(responder.location);
    case EngagementSurveyRollupType.IsManager:
      return responder.isManager ? "Managers" : "ICs";
    case EngagementSurveyRollupType.Manager:
      return assertNonNull(responder.manager?.name);
    case EngagementSurveyRollupType.Organization:
      return "Org";
    case EngagementSurveyRollupType.Collapsed:
      return "Collapsed";
  }
};

export const initEngagementSurveyReportViewOptions: EngagementSurveyReportViewOptions =
  {
    id: null,
    name: "New View",
    isDirty: false,
    filters: {
      type: EngagementSurveyFilterMatchType.MATCH_ALL,
      list: [],
    },
    groupBy: EngagementSurveyReportGroupBy.Team,
    hiddenColumns: [],
  };
